import { useState, useRef, useCallback, useEffect } from 'react';

interface TimelineStripProps {
  onRangeChange: (from: string, to: string) => void;
}

const START_YEAR = 1995;
const END_YEAR = 2026;
const YEARS = Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => START_YEAR + i);

// Approximate event density per year (visual weight)
const DENSITY: Record<number, number> = {
  1995: 10, 1996: 12, 1997: 15, 1998: 25, 1999: 30,
  2000: 40, 2001: 55, 2002: 70, 2003: 60, 2004: 50,
  2005: 65, 2006: 45, 2007: 35, 2008: 80, 2009: 30,
  2010: 25, 2011: 20, 2012: 15, 2013: 12, 2014: 10,
  2015: 40, 2016: 30, 2017: 25, 2018: 20, 2019: 90,
  2020: 50, 2021: 35, 2022: 30, 2023: 55, 2024: 60,
  2025: 70, 2026: 45,
};

const MAX_DENSITY = Math.max(...Object.values(DENSITY));

export default function TimelineStrip({ onRangeChange }: TimelineStripProps) {
  const [rangeStart, setRangeStart] = useState(START_YEAR);
  const [rangeEnd, setRangeEnd] = useState(END_YEAR);
  const [dragging, setDragging] = useState<'start' | 'end' | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const yearFromX = useCallback((clientX: number) => {
    if (!trackRef.current) return START_YEAR;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round(START_YEAR + pct * (END_YEAR - START_YEAR));
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent, handle: 'start' | 'end') => {
    e.preventDefault();
    setDragging(handle);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return;
    const year = yearFromX(e.clientX);
    if (dragging === 'start' && year < rangeEnd) setRangeStart(year);
    if (dragging === 'end' && year > rangeStart) setRangeEnd(year);
  }, [dragging, rangeStart, rangeEnd, yearFromX]);

  const handlePointerUp = useCallback(() => {
    if (dragging) {
      setDragging(null);
      onRangeChange(`${rangeStart}-01-01`, `${rangeEnd}-12-31`);
    }
  }, [dragging, rangeStart, rangeEnd, onRangeChange]);

  const startPct = ((rangeStart - START_YEAR) / (END_YEAR - START_YEAR)) * 100;
  const endPct = ((rangeEnd - START_YEAR) / (END_YEAR - START_YEAR)) * 100;

  return (
    <div className="timeline-strip">
      <div className="timeline-label">{rangeStart}</div>
      <div
        className="timeline-track"
        ref={trackRef}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Density bars */}
        <div className="timeline-bars">
          {YEARS.map((year) => {
            const density = DENSITY[year] || 5;
            const height = (density / MAX_DENSITY) * 100;
            const inRange = year >= rangeStart && year <= rangeEnd;
            return (
              <div
                key={year}
                className={`timeline-bar ${inRange ? 'in-range' : ''}`}
                style={{ height: `${height}%` }}
                title={`${year}: ~${density} events`}
              />
            );
          })}
        </div>

        {/* Selected range highlight */}
        <div
          className="timeline-range"
          style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
        />

        {/* Drag handles */}
        <div
          className="timeline-handle start"
          style={{ left: `${startPct}%` }}
          onPointerDown={(e) => handlePointerDown(e, 'start')}
        />
        <div
          className="timeline-handle end"
          style={{ left: `${endPct}%` }}
          onPointerDown={(e) => handlePointerDown(e, 'end')}
        />
      </div>
      <div className="timeline-label">{rangeEnd}</div>
    </div>
  );
}
